<script lang="ts">
  import InfiniteScroll from '$lib/components/common/InfiniteScroll.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faLoader } from '@itdev-fortawesome/pro-solid-svg-icons/faLoader';
  import { faCheck } from '@itdev-fortawesome/pro-solid-svg-icons/faCheck';
  import { tick } from 'svelte';
  import { faChevronDown } from '@itdev-fortawesome/pro-solid-svg-icons/faChevronDown';
  import { faSearch } from '@itdev-fortawesome/pro-solid-svg-icons/faSearch';

  interface AutocompleteItem {
    value: string;
    /**
     * The text to fill into the search input when an item is selected.
     */
    text: string;
    /**
     * Title text to render in the dropdown menu item.
     */
    title: string;
    /**
     * Subtitle text to render in the dropdown menu item.
     */
    subtitle?: string;
  }

  interface QuickSearchProps {
    placeholder?: string;
    id: string;
    value?: string | null;
    valueText?: string | null;
    minimumQueryLength?: number;
    loadData: (query: string, page: number, pageSize: number, signal: AbortSignal) => Promise<AutocompleteItem[]>;
    emptySearchResultsLabel: string;
    beginSearchingLabel?: string;
    disabled?: boolean;
    invalid?: boolean;
    feedback?: string;
    touched?: boolean;
  }

  let {
    value = $bindable(null),
    valueText,
    placeholder = 'Search',
    id,
    loadData,
    emptySearchResultsLabel,
    beginSearchingLabel,
    disabled = $bindable(false),
    minimumQueryLength = 0,
    invalid = false,
    feedback,
    touched = $bindable(false),
  }: QuickSearchProps = $props();

  let dropdownActive = $state(false);
  let searchTextBoxValue = $state(valueText ?? '');
  let searchQuery = $state(valueText ?? '');
  let quickSearchDiv: HTMLDivElement;
  let input: HTMLInputElement;
  let infiniteScroll: InfiniteScroll<AutocompleteItem>;

  /**
   *
   * @param newvalue
   * @param valueText - The text to set the search input value to. If null, no changes will be made.
   * @param dropdownBehavior - whether to keep the dropdown open or close. If null, no changes will be made.
   */
  function makeSelection(newvalue: string | null, valueText: string | null, dropdownBehavior: 'open' | 'close' | null) {
    value = newvalue;
    if (valueText !== null) {
      searchTextBoxValue = valueText;
      searchQuery = valueText;
    }
    input.focus();
    if (dropdownBehavior === 'close') dropdownActive = false;
    if (dropdownBehavior === 'open') dropdownActive = true;
  }

  function makeSelectionFromEnter(e: KeyboardEvent) {
    if (e.target === input) return;
    const value = (e.target as HTMLElement).dataset.value;
    const text = (e.target as HTMLElement).dataset.text;

    makeSelection(value!, text!, 'close');
  }

  function focusOutHandler(e: FocusEvent) {
    // If the related target is a child of the quick search div, do not close the dropdown. If the related target is
    // null but the active element is a child of the quick search div, do not close the dropdown.

    const relatedTargetParentQuickSearch = (e.relatedTarget as HTMLElement)?.closest('.quick-search');
    if (e.relatedTarget && relatedTargetParentQuickSearch === e.currentTarget) {
      return;
    }

    const activeElementParentQuickSearch = document.activeElement?.closest('.quick-search');
    if (activeElementParentQuickSearch === e.currentTarget) {
      return;
    }

    dropdownActive = false;
    touched = true;
  }

  function clickHandler(e: MouseEvent) {
    let trg = (e.target as HTMLElement).closest('.result-item') as HTMLElement | null;
    if (!trg) {
      focus();
      input.select();
      return;
    }
    const value = trg.dataset.value;
    const text = trg.dataset.text;
    makeSelection(value!, text!, 'close');
  }

  async function keyDownHandler(e: KeyboardEvent) {
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) {
      if (e.key.length === 1 || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        // regular single character key press or arrow key navigation
        input.focus();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      if (await traverse(true)) {
        e.preventDefault();
      }
    } else if (e.key === 'ArrowUp') {
      if (await traverse(false)) {
        e.preventDefault();
      }
    } else if (e.key === 'Enter') {
      if (document.activeElement !== input) {
        // if the input is in focus, we’ll allow native behavior
        e.preventDefault();
        makeSelectionFromEnter(e);
      }
    } else if (e.key === 'Escape') {
      if (dropdownActive) {
        e.preventDefault();
        e.stopImmediatePropagation();
        input.focus();
        dropdownActive = false;
      }
    } else if (e.key !== 'Tab') {
      input.focus();
    }
  }

  function searchTextBoxInputHandler(e: Event & { currentTarget: HTMLInputElement }) {
    dropdownActive = true;
    searchQuery = e.currentTarget.value;
    if (value) {
      makeSelection(null, null, 'open');
    }
  }

  function searchTextBoxFocusHandler(e: FocusEvent) {
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    // prevent reopening the dropdown if the focus event was fired from Escape handled by the above handler
    if (!relatedTarget || relatedTarget?.closest('.quick-search') !== quickSearchDiv) {
      dropdownActive = true;
    }
  }

  /**
   * Traverses the list of autocomplete results.
   * @param forward
   * @returns boolean - True if the key event should be consumed. False if the native key event handler should take over.
   */
  async function traverse(forward: boolean): Promise<boolean> {
    const results = Array.from(quickSearchDiv.querySelectorAll('.result-item'));
    if (forward) {
      dropdownActive = true;
      await tick();
    }

    if (results.length === 0) return false; // nothing to traverse

    const focusedIdx = results.findIndex(el => el === document.activeElement);

    if (focusedIdx === -1) {
      // input is in focus. give focus to first item
      if (forward) {
        (results[0] as HTMLElement).focus();
        return true;
      }
      return false; // up arrow pressed in input. allow native behavior
    }

    if (focusedIdx === 0 && !forward) {
      input.focus();
      return true;
    }

    const nextIdx = (focusedIdx + (forward ? 1 : -1)) % results.length;
    (results[nextIdx] as HTMLElement).focus();
    return true;
  }

  export function focus() {
    input.focus();
    dropdownActive = true;
  }

  export function clearSelection() {
    makeSelection(null, '', 'close');
  }

  export function forceRefresh() {
    infiniteScroll.forceRefresh();
  }
</script>

<!-- {@debug valueText, searchTextBoxValue} -->

<!-- the div itself it not interactable but we're handling all the events here to reduce listeners -->
<!-- svelte-ignore a11y_no_static_element_interactions-->
<div
  bind:this={quickSearchDiv}
  class="quick-search"
  class:is-invalid={invalid}
  onfocusout={focusOutHandler}
  onkeydown={keyDownHandler}
  onclick={clickHandler}
>
  <input
    {id}
    role="combobox"
    aria-controls="{id}_dropdown"
    aria-expanded={dropdownActive}
    aria-autocomplete="both"
    aria-errormessage={invalid ? `${id}_invalid_feedback` : undefined}
    aria-invalid={invalid ? 'true' : 'false'}
    autocomplete="off"
    spellcheck="false"
    type="text"
    {disabled}
    bind:value={searchTextBoxValue}
    bind:this={input}
    oninput={searchTextBoxInputHandler}
    {placeholder}
    class="form-control"
    class:dropdown-active={dropdownActive}
    class:is-invalid={invalid}
    onfocus={searchTextBoxFocusHandler}
  />

  <div class="quick-search-icon" class:icon-disabled={disabled}>
    {#key dropdownActive}
      <FontAwesomeIcon icon={dropdownActive ? faSearch : faChevronDown} />
    {/key}
  </div>
  <div id="{id}_dropdown" class="results-floating-container" class:d-none={!dropdownActive} role="listbox">
    <InfiniteScroll
      bind:this={infiniteScroll}
      queryString={searchQuery}
      {loadData}
      pageSize={10}
      minimumQueryStringLength={minimumQueryLength}
    >
      {#snippet dataRenderer(data, query, shouldShowLoadingIndicator, error, loadingIndicator)}
        {#each data as autocompleteItem (autocompleteItem.value)}
          <div
            class="result-item"
            class:selected={value === autocompleteItem.value}
            role="option"
            aria-selected={value === autocompleteItem.value}
            tabindex="0"
            data-value={autocompleteItem.value}
            data-text={autocompleteItem.text}
          >
            <div class="result-item-label">
              <div class="result-item-title">{autocompleteItem.title}</div>
              {#if autocompleteItem.subtitle !== undefined}
                <div class="result-item-subtitle">{autocompleteItem.subtitle}</div>
              {/if}
            </div>
            <div class="result-item-icon text-primary" class:d-none={value !== autocompleteItem.value}>
              <FontAwesomeIcon icon={faCheck} />
            </div>
          </div>
        {/each}
        {#if error}
          <p class="text-center p-3 fst-italic text-danger mb-0">
            {error}
          </p>
        {:else if shouldShowLoadingIndicator}
          {@render loadingIndicator()}
        {:else if beginSearchingLabel && minimumQueryLength > 0 && (query?.length ?? 0) < minimumQueryLength}
          <p class="text-center p-3 fst-italic text-secondary mb-0">
            {beginSearchingLabel}
          </p>
        {:else if data.length === 0}
          <p class="text-center p-3 fst-italic text-secondary mb-0">
            {emptySearchResultsLabel}
          </p>
        {/if}
      {/snippet}
      {#snippet loadingIndicator()}
        <div class="text-center p-3 text-secondary">
          <FontAwesomeIcon icon={faLoader} spin />
        </div>
      {/snippet}
    </InfiniteScroll>
  </div>
</div>

{#if invalid && feedback}
  <div class="invalid-feedback" id="{id}_invalid_feedback">
    {feedback}
  </div>
{/if}

<style>
  .quick-search {
    position: relative;
  }

  .quick-search .form-control.is-invalid {
    background-position: right 2.25rem center;
  }

  .quick-search-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    translate: 0 -50%;
    color: var(--bs-secondary-color);
    cursor: pointer;
  }

  .form-control {
    padding-right: 2.5rem;
  }

  .icon-disabled {
    color: var(--bs-tertiary-color);
  }

  .form-control.dropdown-active {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  .results-floating-container {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: white;
    border: var(--bs-border-width) solid var(--bs-border-color);
    border-bottom-left-radius: var(--bs-border-radius);
    border-bottom-right-radius: var(--bs-border-radius);
    box-shadow: var(--bs-box-shadow-sm);
    border-top: none;

    max-height: 300px;
    overflow-y: auto;

    z-index: 100;
  }

  .result-item {
    padding: 0.5rem 0.75rem;
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .result-item:hover {
    background-color: var(--bs-secondary-bg);
    cursor: pointer;
  }

  .result-item:focus {
    box-shadow: inset 0 0 0 0.25rem rgba(var(--uams-primary-red-rgb), 0.25);
    outline: none;
  }

  .result-item.selected {
    background-color: var(--bs-primary-bg-subtle);
  }

  .result-item-label {
    flex: 1;
  }

  .result-item-title {
    font-weight: bold;
  }

  .result-item-subtitle {
    color: var(--bs-secondary-color);
    font-size: 0.875em;
  }
</style>
